import { useState, useEffect, useCallback } from 'react'
import { api } from '../context/AuthContext'

const fmtTime  = (d) => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'
const fmtPrice = (n) => Number(n ?? 0) === 0 ? 'Free' : 'Premium'
const fmtBytes = (b) => {
  const gb = Number(b || 0) / 1024 ** 3
  return gb >= 1 ? `${gb.toFixed(1)} GB` : `${(Number(b || 0) / 1024 ** 2).toFixed(0)} MB`
}
const fmtDuration = (secs) => {
  if (!secs) return '—'
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60)
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

const MODERATION_BADGE = {
  passed:  { label: '✓ Passed',    color: '#15803d', bg: '#dff5e8' },
  review:  { label: '⚑ Borderline', color: '#b45309', bg: '#fffbeb' },
  flagged: { label: '⚠ Flagged',   color: '#b91c1c', bg: '#fef2f2' },
  pending: { label: '… Pending',   color: '#9a9a94', bg: '#f3f4f6' },
}

// ── Sub-components ────────────────────────────────────────────────────────────
function StatCard({ label, value, accent }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '16px 20px', flex: 1 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.5px', color: accent || '#111317' }}>{value}</div>
    </div>
  )
}

function ActionBtn({ onClick, color, children, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${color}30`, background: `${color}12`, color, fontSize: 11, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", whiteSpace: 'nowrap', opacity: disabled ? 0.5 : 1 }}>
      {children}
    </button>
  )
}

function EmptyState({ message }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 0' }}>
      <div style={{ fontSize: 40, marginBottom: 10 }}>✅</div>
      <div style={{ fontSize: 15, fontWeight: 700 }}>All caught up</div>
      <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>{message}</div>
    </div>
  )
}

function Pagination({ page, pages, onPrev, onNext }) {
  if (pages <= 1) return null
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
      <button onClick={onPrev} disabled={page === 1} style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid #e9e9e4', background: '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700 }}>← Prev</button>
      <span style={{ fontSize: 12, color: '#9a9a94', alignSelf: 'center', fontFamily: "'JetBrains Mono',monospace" }}>{page} / {pages}</span>
      <button onClick={onNext} disabled={page === pages} style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid #e9e9e4', background: '#fff', cursor: page === pages ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700 }}>Next →</button>
    </div>
  )
}

// ── Video review card ─────────────────────────────────────────────────────────
// onApprove/onReject are only passed for pending_review items (see the
// status filter in the main component below) — the delete action is always
// available regardless of status, since a takedown can be needed on a
// video that's already live, rejected, or stuck failed just as easily as
// one still awaiting first review.
function VideoCard({ item, onApprove, onReject, onPreview, onDelete }) {
  const mod = MODERATION_BADGE[item.moderation_status] || MODERATION_BADGE.pending
  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderLeft: `3px solid ${mod.color}`, borderRadius: 10, padding: '14px 16px', display: 'flex', gap: 12 }}>
      <div onClick={() => onPreview(item)} style={{ width: 90, height: 60, borderRadius: 8, background: '#111317', flexShrink: 0, overflow: 'hidden', position: 'relative', cursor: 'pointer' }}>
        {item.thumbnail_url
          ? <img src={item.thumbnail_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />
          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color: 'rgba(255,255,255,0.4)' }}>🎬</div>
        }
        <div style={{ position: 'absolute', bottom: 3, right: 4, fontSize: 9, fontWeight: 700, color: '#fff', background: 'rgba(0,0,0,0.6)', borderRadius: 3, padding: '1px 4px' }}>{fmtDuration(item.duration_secs)}</div>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
          <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</div>
          <span style={{ fontSize: 10, fontFamily: "'JetBrains Mono',monospace", color: '#9a9a94', whiteSpace: 'nowrap' }}>{fmtTime(item.created_at)}</span>
        </div>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
          by {item.creator_name || item.creator_username || '—'} · {item.category || 'Uncategorized'} · <span style={{ fontWeight: 700, color: '#111317' }}>{fmtPrice(item.price_amount)}</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: mod.bg, color: mod.color, fontFamily: "'JetBrains Mono',monospace" }}>{mod.label}</span>
          {item.moderation_detail && <span style={{ fontSize: 11, color: '#9a9a94' }}>{item.moderation_detail}</span>}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <ActionBtn onClick={() => onPreview(item)} color="#0369a1">▶ Preview</ActionBtn>
          {onApprove && <ActionBtn onClick={() => onApprove(item)} color="#15803d">✓ Approve</ActionBtn>}
          {onReject  && <ActionBtn onClick={() => onReject(item)} color="#b91c1c">✕ Reject</ActionBtn>}
          <ActionBtn onClick={() => onDelete(item)} color="#991b1b">🗑 Delete</ActionBtn>
        </div>
      </div>
    </div>
  )
}

// ── Preview modal — fetches a real playback URL (admin gets full access,
// same entitlement bypass as the video's owner) rather than just the public
// preview clip, so moderation can review the whole runtime, not just the
// first few minutes ──
function PreviewModal({ item, onClose }) {
  const [url, setUrl] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!item) return
    setUrl(null); setError(null)
    api.get(`/videos/${item.video_id}/playback-url`)
      .then(res => {
        if (res.data?.full) setUrl(res.data.playbackUrl)
        else setError('Full playback is not available yet.')
      })
      .catch(err => setError(err.response?.data?.error || 'Could not load video.'))
  }, [item])

  if (!item) return null
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 500 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 640, maxWidth: '90vw', background: '#111317', borderRadius: 16, zIndex: 501, boxShadow: '0 24px 80px rgba(0,0,0,0.5)', overflow: 'hidden', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
        <div style={{ width: '100%', aspectRatio: '16/9', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {url
            ? <video src={url} controls autoPlay style={{ width: '100%', height: '100%' }} />
            : <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>{error || 'Loading…'}</div>
          }
        </div>
        <div style={{ padding: '14px 18px' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 3 }}>{item.title}</div>
          <div style={{ fontSize: 12, color: '#9a9a94', marginBottom: 14 }}>by {item.creator_name || item.creator_username}</div>
          <button onClick={onClose} style={{ width: '100%', height: 40, borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: '#e5e7eb', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Close</button>
        </div>
      </div>
    </>
  )
}

function RejectModal({ item, onClose, onConfirm, loading }) {
  const [note, setNote] = useState('')
  if (!item) return null
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 400 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 420, background: '#fff', borderRadius: 14, zIndex: 401, boxShadow: '0 20px 60px rgba(0,0,0,0.15)', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
        <div style={{ padding: '24px 24px 8px' }}>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 4 }}>Reject "{item.title}"?</div>
          <div style={{ fontSize: 13, color: '#9a9a94', marginBottom: 14 }}>The creator will see this reason. Be specific.</div>
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={4} placeholder="e.g. Contains copyrighted content not owned by the uploader"
            style={{ width: '100%', boxSizing: 'border-box', borderRadius: 8, border: '1px solid #e9e9e4', padding: '10px 12px', fontSize: 13, resize: 'vertical', outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
        </div>
        <div style={{ padding: '16px 24px 24px', display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
          <button onClick={() => onConfirm(note)} disabled={loading || !note.trim()}
            style={{ flex: 1, height: 42, borderRadius: 8, border: 'none', background: loading || !note.trim() ? '#ccc' : '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: loading || !note.trim() ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            {loading ? 'Working…' : 'Reject Video'}
          </button>
        </div>
      </div>
    </>
  )
}

// ── Delete confirm modal — fetches purchase/refund history BEFORE deleting
// so the admin sees the impact up front, instead of only learning about it
// from an alert() after the video is already gone. Admin's delete power
// stays fully unrestricted either way — this is warning UX, not a gate. ──
function DeleteModal({ item, onClose, onConfirm, loading }) {
  const [preview, setPreview] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(true)

  useEffect(() => {
    if (!item) return
    setPreview(null); setPreviewLoading(true)
    api.get(`/videos/admin/${item.video_id}/delete-preview`)
      .then(res => setPreview(res.data))
      .catch(() => setPreview(null))
      .finally(() => setPreviewLoading(false))
  }, [item])

  if (!item) return null
  const hasHistory = preview && (preview.completed_count > 0 || preview.refunded_count > 0 || preview.refund_requested_count > 0)

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 400 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 440, background: '#fff', borderRadius: 14, zIndex: 401, boxShadow: '0 20px 60px rgba(0,0,0,0.15)', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
        <div style={{ padding: '24px 24px 8px' }}>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 4 }}>Permanently delete "{item.title}"?</div>
          <div style={{ fontSize: 13, color: '#9a9a94', marginBottom: 14 }}>This cannot be undone — the file, likes, and comments are removed immediately.</div>

          {item.status === 'ready' && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#b91c1c', marginBottom: 10 }}>
              ⚠ This video is LIVE and may already have paying viewers — they will lose access.
            </div>
          )}

          {previewLoading ? (
            <div style={{ fontSize: 12, color: '#9a9a94', padding: '4px 0 10px' }}>Checking purchase history…</div>
          ) : hasHistory ? (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 4 }}>⚠ This video has purchase history:</div>
              <div style={{ fontSize: 12, color: '#92400e', lineHeight: 1.6 }}>
                {preview.completed_count > 0 && <div>{preview.completed_count} completed purchase{preview.completed_count !== 1 ? 's' : ''} — buyer{preview.completed_count !== 1 ? 's' : ''} will lose access and the payment record will be erased</div>}
                {preview.refunded_count > 0 && <div>{preview.refunded_count} refunded purchase{preview.refunded_count !== 1 ? 's' : ''} — that refund record will be erased</div>}
                {preview.refund_requested_count > 0 && <div>{preview.refund_requested_count} pending refund request{preview.refund_requested_count !== 1 ? 's' : ''} — will be erased before resolution</div>}
              </div>
            </div>
          ) : null}
        </div>
        <div style={{ padding: '16px 24px 24px', display: 'flex', gap: 10 }}>
          <button onClick={onClose} disabled={loading} style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
          <button onClick={onConfirm} disabled={loading || previewLoading}
            style={{ flex: 1, height: 42, borderRadius: 8, border: 'none', background: loading || previewLoading ? '#ccc' : '#991b1b', color: '#fff', fontSize: 13, fontWeight: 700, cursor: loading || previewLoading ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            {loading ? 'Deleting…' : 'Delete Permanently'}
          </button>
        </div>
      </div>
    </>
  )
}

// ── Storage usage table ───────────────────────────────────────────────────────
function StorageUsageTab() {
  const [rows, setRows] = useState([])
  const [softCap, setSoftCap] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api.get('/videos/admin/storage-usage')
      .then(res => { setRows(res.data?.creators ?? []); setSoftCap(res.data?.soft_cap_bytes ?? 0) })
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '60px 0' }}>Loading storage usage…</div>
  if (rows.length === 0) return <EmptyState message="No creators have uploaded long-form videos yet." />

  return (
    <div>
      <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginBottom: 14 }}>
        Soft cap: {fmtBytes(softCap)} per creator — flagged for visibility, not enforced automatically
      </div>
      <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          <thead>
            <tr style={{ background: '#f9f9f7', borderBottom: '1px solid #e9e9e4' }}>
              {['Creator', 'Videos', 'Storage Used', '% of soft cap', ''].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const pct = softCap ? Math.min(100, Math.round((r.total_bytes / softCap) * 100)) : 0
              return (
                <tr key={r.creator_id} style={{ borderBottom: '1px solid #f0f0ec' }}>
                  <td style={{ padding: '10px 16px', fontSize: 13, fontWeight: 700 }}>{r.creator_name || r.creator_username || `Creator #${r.creator_id}`}</td>
                  <td style={{ padding: '10px 16px', fontSize: 13, fontFamily: "'JetBrains Mono',monospace" }}>{r.video_count}</td>
                  <td style={{ padding: '10px 16px', fontSize: 13, fontFamily: "'JetBrains Mono',monospace" }}>{fmtBytes(r.total_bytes)}</td>
                  <td style={{ padding: '10px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 80, height: 6, background: '#f0f0ec', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: r.over_soft_cap ? '#b91c1c' : '#15803d', borderRadius: 4 }} />
                      </div>
                      <span style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{pct}%</span>
                    </div>
                  </td>
                  <td style={{ padding: '10px 16px' }}>
                    {r.over_soft_cap && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: '#fef2f2', color: '#b91c1c', fontFamily: "'JetBrains Mono',monospace" }}>⚠ OVER CAP</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Main page ──────────────────────────────────────────────────────────────────
const STATUS_FILTERS = [
  { value: 'pending_review', label: 'Pending Review' },
  { value: 'ready',          label: 'Live' },
  { value: 'rejected',       label: 'Rejected' },
  { value: 'failed',         label: 'Failed' },
  { value: 'all',            label: 'All' },
]

export default function VideoModeration() {
  const [tab, setTab] = useState('queue') // 'queue' | 'storage'
  const [statusFilter, setStatusFilter] = useState('pending_review')

  const [videos,  setVideos]  = useState([])
  const [loading, setLoading] = useState(true)
  const [page,    setPage]    = useState(1)
  const [pages,   setPages]   = useState(1)
  const [total,   setTotal]   = useState(0)

  const [preview, setPreview] = useState(null)
  const [rejecting, setRejecting] = useState(null)
  const [deleting, setDeleting] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)

  const PER_PAGE = 20

  const fetchQueue = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ page, limit: PER_PAGE, status: statusFilter })
      const res = await api.get(`/videos/admin?${params}`)
      setVideos(res.data?.videos ?? [])
      setTotal(res.data?.videos?.length ?? 0)
      setPages(1) // backend doesn't return a total count for this endpoint yet — single page for now
    } catch {
      setVideos([])
    } finally { setLoading(false) }
  }, [page, statusFilter])

  useEffect(() => { if (tab === 'queue') fetchQueue() }, [tab, fetchQueue])
  useEffect(() => { setPage(1) }, [statusFilter])

  const handleApprove = async (item) => {
    try {
      await api.post(`/videos/admin/${item.video_id}/approve`)
      setVideos(prev => prev.filter(v => v.video_id !== item.video_id))
      setTotal(t => Math.max(0, t - 1))
    } catch (err) {
      alert(err.response?.data?.error || 'Could not approve')
    }
  }

  const confirmReject = async (note) => {
    if (!rejecting) return
    setActionLoading(true)
    try {
      await api.post(`/videos/admin/${rejecting.video_id}/reject`, { admin_note: note.trim() })
      setVideos(prev => prev.filter(v => v.video_id !== rejecting.video_id))
      setTotal(t => Math.max(0, t - 1))
      setRejecting(null)
    } catch (err) {
      alert(err.response?.data?.error || 'Could not reject')
    } finally { setActionLoading(false) }
  }

  const confirmDelete = async () => {
    if (!deleting) return
    setActionLoading(true)
    try {
      await api.delete(`/videos/admin/${deleting.video_id}`)
      setVideos(prev => prev.filter(v => v.video_id !== deleting.video_id))
      setTotal(t => Math.max(0, t - 1))
      setDeleting(null)
    } catch (err) {
      alert(err.response?.data?.error || 'Could not delete')
    } finally { setActionLoading(false) }
  }

  const TABS = [
    { id: 'queue',   label: '🎬 Review Queue',  count: total },
    { id: 'storage', label: '💾 Storage Usage', count: 0 },
  ]

  return (
    <div style={{ padding: '28px 32px 60px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>🎬 Video Moderation</div>
        <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>Long-form uploads (movies, episodes, music videos) always require manual review before going live — never auto-published.</div>
      </div>

      <div style={{ display: 'flex', gap: 14, marginBottom: 24 }}>
        <StatCard label={STATUS_FILTERS.find(f => f.value === statusFilter)?.label ?? 'Videos'} value={total} accent="#b45309" />
      </div>

      <div style={{ display: 'flex', borderBottom: '1px solid #e9e9e4', marginBottom: 20, gap: 4 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding: '9px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif", color: tab === t.id ? '#111317' : '#9a9a94', borderBottom: `2px solid ${tab === t.id ? '#e8440a' : 'transparent'}`, marginBottom: -1, display: 'flex', alignItems: 'center', gap: 6 }}>
            {t.label}
            {t.count > 0 && (
              <span style={{ background: tab === t.id ? '#e8440a' : '#e9e9e4', color: tab === t.id ? '#fff' : '#6b7280', fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 10, fontFamily: "'JetBrains Mono',monospace" }}>{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {tab === 'queue' && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 12, fontWeight: 700, color: '#111317', fontFamily: "'Plus Jakarta Sans',sans-serif", cursor: 'pointer' }}>
              {STATUS_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '60px 0' }}>Loading videos…</div>
          ) : videos.length === 0 ? (
            <EmptyState message={statusFilter === 'pending_review' ? 'No videos waiting for review.' : 'No videos match this filter.'} />
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {videos.map(item => (
                  <VideoCard key={item.video_id} item={item}
                    onApprove={statusFilter === 'pending_review' ? handleApprove : null}
                    onReject={statusFilter === 'pending_review' ? setRejecting : null}
                    onPreview={setPreview} onDelete={setDeleting} />
                ))}
              </div>
              <Pagination page={page} pages={pages} onPrev={() => setPage(p => p - 1)} onNext={() => setPage(p => p + 1)} />
            </>
          )}
        </div>
      )}

      {tab === 'storage' && <StorageUsageTab />}

      <PreviewModal item={preview} onClose={() => setPreview(null)} />
      <RejectModal item={rejecting} onClose={() => setRejecting(null)} onConfirm={confirmReject} loading={actionLoading} />
      <DeleteModal item={deleting} onClose={() => setDeleting(null)} onConfirm={confirmDelete} loading={actionLoading} />
    </div>
  )
}
